import { useState, useEffect } from "react";
import {
  Search,
  Download,
  Eye,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  User,
  Save,
  Copy,
  Clock,
  Users,
  Ribbon,
  Check,
  CarFront,
  PlaneLanding,
  PlaneTakeoff,
  Plane,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  formatFranceDateTime,
  toParisISO,
  parseParisDatetime,
} from "@/lib/time-utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });
      if (statusFilter !== "all") params.set("status", statusFilter);

      const response = await fetch(`${API_URL}/api/admin/bookings?${params}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter(
    (booking) =>
      booking.booking_id?.toLowerCase().includes(search.toLowerCase()) ||
      booking.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      booking.customer_email?.toLowerCase().includes(search.toLowerCase()),
  );

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!selectedBooking) return;

    const arrivalText =
      !isOneWay || isArrival
        ? `🛫 ARRIVAL\nFlight: ${selectedBooking.arrival_flight_no || "N/A"}\nFrom: ${selectedBooking.arrival_from || "N/A"}`
        : null;

    const departureText =
      !isOneWay || isDeparture
        ? `🛬 DEPARTURE\nFlight: ${selectedBooking.departure_flight_no || "N/A"}\nTo: ${selectedBooking.departure_to || "N/A"}`
        : null;

    let text = `✨ BOOKING DETAILS: ${selectedBooking.booking_id}\n`;
    text += `Vehicle: ${selectedBooking.vehicle_type}\n\n`;

    text += `👤 CUSTOMER INFO\n`;
    text += `Name: ${selectedBooking.customer_name}\n`;
    text += `Email: ${selectedBooking.customer_email}\n`;
    text += `Phone: ${selectedBooking.customer_phone}\n\n`;

    text += `📍 ROUTE INFO\n`;
    text += `From: ${selectedBooking.pickup_location}\n`;
    text += `To: ${selectedBooking.dropoff_location}\n`;
    text += `Passengers: ${selectedBooking.adults} Adults, ${selectedBooking.kids} Kids\n`;
    text += `Pickup: ${formatFranceDateTime(selectedBooking.pickup_datetime, "full")}\n\n`;

    if (selectedBooking.trip_type === "return") {
      text += `🔄 RETURN ROUTE\n`;
      text += `From: ${selectedBooking.return_pickup_location}\n`;
      text += `To: ${selectedBooking.return_dropoff_location}\n`;
      text += `Return Pickup: ${formatFranceDateTime(selectedBooking.return_pickup_datetime, "full")}\n\n`;
    }

    if (arrivalText || departureText) {
      text += `✈️ FLIGHT INFORMATION\n`;
      if (arrivalText) text += `${arrivalText}\n`;
      if (departureText) text += `${departureText}\n`;
      text += `\n`;
    }

    text += `💳 PAYMENT\n`;
    text += `Base Price: €${selectedBooking.base_price}\n`;
    if (selectedBooking.night_surcharge > 0) {
      text += `Night Surcharge: €${selectedBooking.night_surcharge}\n`;
    }
    text += `Total Price: €${selectedBooking.total_price}\n`;
    text += `Method: Pay by ${selectedBooking.payment_method}\n\n`;

    if (selectedBooking.notes) {
      text += `📝 NOTES\n`;
      text += `"${selectedBooking.notes}"`;
    }

    navigator.clipboard
      .writeText(text.trim())
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?"))
      return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/bookings/${bookingId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        toast.success("Booking deleted successfully");
        fetchBookings();
      } else {
        toast.error("Failed to delete booking");
      }
    } catch {
      toast.error("Error deleting booking");
    }
  };

  const handleEditBooking = (booking) => {
    const { date, time } = parseParisDatetime(booking.pickup_datetime);
    setEditingBooking({ ...booking, pickup_date: date, pickup_time: time });
  };

  const handleSaveBooking = async () => {
    if (!editingBooking) return;
    setIsSaving(true);
    try {
      const updateData = {
        customer_name: editingBooking.customer_name,
        customer_email: editingBooking.customer_email,
        customer_phone: editingBooking.customer_phone,
        pickup_location: editingBooking.pickup_location,
        dropoff_location: editingBooking.dropoff_location,
        adults: parseInt(editingBooking.adults) || 1,
        kids: parseInt(editingBooking.kids) || 0,
        status: editingBooking.status,
        notes: editingBooking.notes || "",
      };

      if (editingBooking.pickup_date && editingBooking.pickup_time) {
        updateData.pickup_datetime = toParisISO(
          editingBooking.pickup_date,
          editingBooking.pickup_time,
        );
      }

      const response = await fetch(
        `${API_URL}/api/admin/bookings/${editingBooking.booking_id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData),
        },
      );

      if (response.ok) {
        toast.success("Booking updated successfully");
        setEditingBooking(null);
        fetchBookings();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update booking");
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Error updating booking");
    } finally {
      setIsSaving(false);
    }
  };

  const isOneWay = selectedBooking?.trip_type === "oneway";
  const isArrival = selectedBooking?.flight_detail_type === "ARRIVAL";
  const isDeparture = selectedBooking?.flight_detail_type === "DEPARTURE";

  return (
    <div data-testid="admin-bookings">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">
            Bookings
          </h1>
          <p className="text-slate-600">Manage all transfer bookings</p>
        </div>
        <Button className="bg-primary-600 hover:bg-primary-700">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by booking ID, name, or email..."
                className="pl-10"
                data-testid="booking-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="w-full md:w-[180px]"
                data-testid="status-filter"
              >
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              Loading bookings...
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No bookings found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-600">
                      <th className="px-6 py-4 font-semibold">Booking ID</th>
                      <th className="px-6 py-4 font-semibold">Customer</th>
                      <th className="px-6 py-4 font-semibold">Route</th>
                      <th className="px-6 py-4 font-semibold">Date & Time</th>
                      <th className="px-6 py-4 font-semibold">Amount</th>
                      <th className="px-6 py-4 font-semibold">Status</th>
                      <th className="px-6 py-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id}>
                        <td className="px-6 py-4 font-medium text-primary-600">
                          {booking.booking_id}
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900">
                            {booking.customer_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {booking.customer_email}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-slate-600 text-sm">
                          <div className="max-w-[200px] truncate">
                            {booking.pickup_location} →{" "}
                            {booking.dropoff_location}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {formatFranceDateTime(
                            booking.pickup_datetime,
                            "date",
                          )}
                          <br />
                          <span className="text-sm">
                            {formatFranceDateTime(
                              booking.pickup_datetime,
                              "time",
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-semibold text-slate-900">
                          €{booking.total_price}
                        </td>
                        <td className="px-6 py-4">
                          {booking.status === "PENDING" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              <Clock className="w-3 h-3" />
                              Pending
                            </span>
                          ) : booking.status === "CANCELLED" ? (
                            <span className="inline-flex px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              Cancelled
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              Confirmed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedBooking(booking)}
                              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                              title="View details"
                            >
                              <Eye className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              onClick={() => handleEditBooking(booking)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit booking"
                              data-testid={`edit-booking-${booking.booking_id}`}
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteBooking(booking.booking_id)
                              }
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                <p className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedBooking}
        onOpenChange={() => setSelectedBooking(null)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="border-b pb-4 shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-xl">
                Booking Details - {selectedBooking?.booking_id}
              </DialogTitle>

              <button
                onClick={handleCopy}
                disabled={copied}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-300 border ${
                  copied
                    ? "text-green-600 border-green-200 bg-green-50"
                    : "text-slate-600 border-slate-200 hover:text-primary-600 hover:bg-slate-100"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy Details</span>
                  </>
                )}
              </button>
            </div>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-6 overflow-y-auto pr-2 py-4">
              <h3 className="font-semibold text-slate-900 p-0 flex items-center gap-2 text-lg">
                <CarFront className="w-5 h-5 text-primary-600" />
                Vehicle:{" "}
                <span className="font-normal text-slate-700">
                  {selectedBooking.vehicle_type}
                </span>
              </h3>

              <div className="grid md:grid-cols-2 gap-6 border-b border-slate-200 pb-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 uppercase text-xs tracking-wider">
                    <User className="w-4 h-4 text-primary-600" />
                    Customer Info
                  </h3>
                  <div className="space-y-2 text-sm p-3 rounded-lg">
                    <p>
                      <span className="text-slate-500">Name:</span>{" "}
                      {selectedBooking.customer_name}
                    </p>
                    <p>
                      <span className="text-slate-500">Email:</span>{" "}
                      {selectedBooking.customer_email}
                    </p>
                    <p>
                      <span className="text-slate-500">Phone:</span>{" "}
                      {selectedBooking.customer_phone}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 uppercase text-xs tracking-wider">
                    <Ribbon className="w-4 h-4 text-primary-600" />
                    Luggage
                  </h3>
                  <div className="space-y-2 text-sm p-3 rounded-lg">
                    <p>
                      <span className="text-slate-500">Hand luggage:</span>{" "}
                      {selectedBooking.hand_luggages}
                    </p>
                    <p>
                      <span className="text-slate-500">Checked luggage:</span>{" "}
                      {selectedBooking.checked_luggages}
                    </p>
                  </div>
                </div>
              </div>

              {/* Route Info */}
              <div className="grid md:grid-cols-2 gap-6 border-b border-slate-200 pb-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 uppercase text-xs tracking-wider">
                    <MapPin className="w-4 h-4 text-primary-600" />
                    Route Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-slate-500">From:</span>{" "}
                      {selectedBooking.pickup_location}
                    </p>
                    <p>
                      <span className="text-slate-500">To:</span>{" "}
                      {selectedBooking.dropoff_location}
                    </p>
                    <p>
                      <span className="text-slate-500">Passengers:</span>{" "}
                      {`${selectedBooking.adults} ${selectedBooking.adults === 1 ? "Adult" : "Adults"}, ${selectedBooking.kids} ${selectedBooking.kids === 1 ? "Kid" : "Kids"}`}
                    </p>
                  </div>
                </div>
                {selectedBooking.trip_type === "return" && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 uppercase text-xs tracking-wider">
                      <MapPin className="w-4 h-4 text-primary-600" />
                      Return Route Info
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="text-slate-500">From:</span>{" "}
                        {selectedBooking.return_pickup_location}
                      </p>
                      <p>
                        <span className="text-slate-500">To:</span>{" "}
                        {selectedBooking.return_dropoff_location}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Flight Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 uppercase text-xs tracking-wider">
                  <Plane className="w-4 h-4 text-primary-600" />
                  Flight Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(!isOneWay || isArrival) && (
                    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                      <div className="bg-background px-4 py-2 border-b flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest">
                          Arrival
                        </span>
                        <PlaneLanding className="w-3 h-3" />
                      </div>
                      <div className="p-4 space-y-3">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">
                            Flight No
                          </p>
                          <p className="text-sm font-bold text-slate-800 tracking-tight">
                            {selectedBooking.arrival_flight_no || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">
                            Origin
                          </p>
                          <p className="text-sm font-semibold text-slate-700">
                            {selectedBooking.arrival_from || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {(!isOneWay || isDeparture) && (
                    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                      <div className="bg-background px-4 py-2 border-b flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest">
                          Departure
                        </span>
                        <PlaneTakeoff className="w-3 h-3" />
                      </div>
                      <div className="p-4 space-y-3">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">
                            Flight No
                          </p>
                          <p className="text-sm font-bold text-slate-800 tracking-tight">
                            {selectedBooking.departure_flight_no || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">
                            Destination
                          </p>
                          <p className="text-sm font-semibold text-slate-700">
                            {selectedBooking.departure_to || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Schedule + Payment */}
              <div className="grid md:grid-cols-2 gap-6 pt-4">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 uppercase text-xs tracking-wider">
                    <Calendar className="w-4 h-4 text-primary-600" />
                    Schedule
                  </h3>
                  <div className="space-y-2 text-sm p-3 rounded-lg">
                    <p>
                      <span className="text-slate-500">Trip Type:</span>{" "}
                      <span className="capitalize">
                        {selectedBooking.trip_type}
                      </span>
                    </p>
                    <br />

                    <p>
                      <span className="text-slate-500">Pickup:</span>{" "}
                      {formatFranceDateTime(
                        selectedBooking.pickup_datetime,
                        "full",
                      )}
                    </p>
                    {selectedBooking.return_pickup_datetime && (
                      <>
                        <br />
                        <p>
                          <span className="text-slate-500">Return Pickup:</span>{" "}
                          {formatFranceDateTime(
                            selectedBooking.return_pickup_datetime,
                            "full",
                          )}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 uppercase text-xs tracking-wider">
                    Payment
                  </h3>
                  <div className="space-y-2 text-sm p-3 rounded-lg">
                    <p>
                      <span className="text-slate-500">Base Price:</span> €
                      {selectedBooking.base_price}
                    </p>
                    {selectedBooking.night_surcharge > 0 && (
                      <p>
                        <span className="text-slate-500">Night Surcharge:</span>{" "}
                        €{selectedBooking.night_surcharge}
                      </p>
                    )}
                    <p className="font-bold text-lg text-primary-600 pt-1 border-t">
                      Total: €{selectedBooking.total_price}
                    </p>
                    <p>
                      <span className="text-slate-500">Method:</span> Pay by{" "}
                      {selectedBooking.payment_method}
                    </p>
                  </div>
                </div>
              </div>

              {selectedBooking.notes && (
                <div className="pt-2">
                  <h3 className="font-semibold text-slate-900 mb-2 uppercase text-xs tracking-wider">
                    Notes
                  </h3>
                  <p className="text-sm text-slate-600 bg-amber-50 p-4 rounded-lg border border-amber-100 italic">
                    "{selectedBooking.notes}"
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog
        open={!!editingBooking}
        onOpenChange={() => setEditingBooking(null)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary-600" />
              Edit Booking - {editingBooking?.booking_id}
            </DialogTitle>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-primary-600" />
                  Customer Information
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Name
                    </Label>
                    <Input
                      value={editingBooking.customer_name || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          customer_name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Phone
                    </Label>
                    <Input
                      value={editingBooking.customer_phone || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          customer_phone: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={editingBooking.customer_email || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          customer_email: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Route Info */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary-600" />
                  Route Information
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Pickup Location
                    </Label>
                    <Input
                      value={editingBooking.pickup_location || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          pickup_location: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Dropoff Location
                    </Label>
                    <Input
                      value={editingBooking.dropoff_location || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          dropoff_location: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Schedule — inputs show Paris local time */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary-600" />
                  Schedule
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Date
                    </Label>
                    <Input
                      type="date"
                      value={editingBooking.pickup_date || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          pickup_date: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Time
                    </Label>
                    <Input
                      type="time"
                      value={editingBooking.pickup_time || ""}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          pickup_time: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Passengers & Status */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary-600" />
                  Passengers & Status
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Adults
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={editingBooking.adults || 1}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          adults: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Kids
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingBooking.kids || 0}
                      onChange={(e) =>
                        setEditingBooking({
                          ...editingBooking,
                          kids: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Status
                    </Label>
                    <Select
                      value={editingBooking.status || "confirmed"}
                      onValueChange={(v) =>
                        setEditingBooking({ ...editingBooking, status: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Notes
                </Label>
                <Textarea
                  value={editingBooking.notes || ""}
                  onChange={(e) =>
                    setEditingBooking({
                      ...editingBooking,
                      notes: e.target.value,
                    })
                  }
                  placeholder="Special requests or notes..."
                  rows={3}
                />
              </div>

              <div className="p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Total Price</span>
                  <span className="font-heading font-bold text-xl text-primary-600">
                    €{editingBooking.total_price}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setEditingBooking(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveBooking}
                  disabled={isSaving}
                  className="bg-primary-600 hover:bg-primary-700"
                  data-testid="save-booking-btn"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
